import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUserWeeklyBreakdown } from '@/api/query/useLeaderboard';
import { breakdownGridClass, DEFAULT_VISIBLE_CURRENCIES } from './currencyColumns';

interface WeeklyBreakdownListProps {
  userId: string;
  leagueCode?: string;
  isCurrentUser?: boolean;
}

export const WeeklyBreakdownList: React.FC<WeeklyBreakdownListProps> = ({
  userId,
  leagueCode,
  isCurrentUser = false,
}) => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useUserWeeklyBreakdown(userId, leagueCode);

  if (isLoading) {
    return (
      <div className={`text-sm ${isCurrentUser ? 'text-white/70' : 'text-text-secondary'}`}>
        Loading weekly breakdown...
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-sm ${isCurrentUser ? 'text-white/70' : 'text-text-secondary'}`}>
        Failed to load weekly breakdown
      </div>
    );
  }

  if (!data?.weeks || data.weeks.length === 0) {
    return (
      <div className={`text-sm ${isCurrentUser ? 'text-white/70' : 'text-text-secondary'}`}>
        No weekly data available
      </div>
    );
  }

  // DKPLAT-618: only render Cash/Crowns columns when a pick'em in this period
  // actually uses them (SkyPicks pays Other only). Grid tracks adapt to match.
  const currencies = data.visibleCurrencies ?? DEFAULT_VISIBLE_CURRENCIES;
  const gridClass = breakdownGridClass(currencies);

  return (
    <div>
      {/* Column header */}
      <div
        className={`grid ${gridClass} gap-2 text-xs font-medium mb-2 pb-2 border-b ${
          isCurrentUser ? 'border-white/20' : 'border-border/20'
        }`}
      >
        <span>{t('leaderboard.week')}</span>
        <span className="text-center">{t('leaderboard.rank')}</span>
        {currencies.cash && <span>Cash</span>}
        {currencies.crowns && <span className="hidden sm:block">Crowns</span>}
        <span className="hidden sm:block">Other</span>
        <span className="text-center">{t('leaderboard.points')}</span>
      </div>

      <div className="space-y-1">
        {data.weeks.map(week => (
          <div
            key={week.week_number}
            className={`grid ${gridClass} gap-2 text-sm py-2 border-b ${
              isCurrentUser ? 'border-white/10' : 'border-border/10'
            } last:border-0`}
          >
            {/* Week Label */}
            <span className={`${isCurrentUser ? 'text-white' : 'text-text-primary'}`}>
              {week.week_label}
            </span>

            {/* Rank */}
            <span
              className={`text-center ${isCurrentUser ? 'text-white/90' : 'text-text-secondary'}`}
            >
              {week.rank !== null ? `#${week.rank}` : '-'}
            </span>

            {/* Cash - always visible when the currency is in use */}
            {currencies.cash && (
              <span
                className={`font-bold text-xs ${isCurrentUser ? 'text-white' : 'text-green-500'}`}
              >
                {week.prize?.cash && week.prize.cash > 0 ? (
                  `$${week.prize.cash.toLocaleString()}`
                ) : (
                  <span className={isCurrentUser ? 'text-white/40' : 'text-text-secondary/40'}>
                    -
                  </span>
                )}
              </span>
            )}

            {/* Crowns - hidden on mobile */}
            {currencies.crowns && (
              <span
                className={`hidden sm:block text-xs ${
                  isCurrentUser ? 'text-white/80' : 'text-yellow-500'
                }`}
              >
                {week.prize?.crowns && week.prize.crowns > 0 ? (
                  `${week.prize.crowns.toLocaleString()}c`
                ) : (
                  <span className={isCurrentUser ? 'text-white/40' : 'text-text-secondary/40'}>
                    -
                  </span>
                )}
              </span>
            )}

            {/* Other - hidden on mobile */}
            <span
              className={`hidden sm:block text-xs truncate ${
                isCurrentUser ? 'text-white/80' : 'text-blue-400'
              }`}
              title={week.prize?.other || ''}
            >
              {week.prize?.other ? (
                week.prize.other
              ) : (
                <span className={isCurrentUser ? 'text-white/40' : 'text-text-secondary/40'}>
                  -
                </span>
              )}
            </span>

            {/* Points */}
            <span
              className={`text-center font-medium ${isCurrentUser ? 'text-white' : 'text-text-primary'}`}
            >
              {week.points}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
